import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, Select } from '@xsolla/ui-core';
import { Search } from '@xsolla/ui-icons';

import { SocialConnection, SocialConnectionsResponse } from '../../../api';
import { usePlatformSettings } from '../../../contexts/settings';
import { Preloader } from '../../ui';
import SocialNetworkCard from './SocialNetworkCard';
import {
  CardWrapper,
  RigthContentWrapper,
  SearchBarWrapper,
  ShowModeButtonWrapper,
  SocialModeSelectWrapper,
  SocialSearchInput
} from './style';

type Props = {
  projectId: string;
};

enum SocialSelectTypes {
  all = 'all',
  connected = 'connected',
  disconnected = 'disconnected'
}

export type ChosenSocials = {
  [key: string]: SocialConnection | null;
};

const PlatformsList = ({ projectId }: Props): JSX.Element => {
  const intl = useIntl();
  const [filteredPlatforms, setFilteredPlatforms] = useState<SocialConnectionsResponse | null>();
  const [connectionsMode, setConnectionsMode] = useState<SocialSelectTypes>(SocialSelectTypes.all);
  const [isShowMoreMode, setIsShowMoreMode] = useState<boolean>(false);
  const { platformSettings, platformSettingsFetching, setPlatformSettings } =
    usePlatformSettings(projectId);

  useEffect(() => {
    if (!platformSettings) {
      return;
    }
    setFilteredPlatforms(platformSettings);
  }, [platformSettings]);

  const selectSocialOptions = [
    {
      children: intl.messages['settings.platform-settings.select.all'],
      value: SocialSelectTypes.all,
      'data-test-id': 'filter-all'
    },
    {
      children: intl.messages['social-connection.select.connected'],
      value: SocialSelectTypes.connected,
      'data-test-id': 'filter-connected'
    },
    {
      children: intl.messages['social-connection.select.disconnected'],
      value: SocialSelectTypes.disconnected,
      'data-test-id': 'filter-disconnected'
    }
  ];

  const handleSelectMode = useCallback(
    (e) => {
      setConnectionsMode(e);
      switch (e) {
        case SocialSelectTypes.connected:
          setFilteredPlatforms(platformSettings?.filter((item) => item.connected));
          return;
        case SocialSelectTypes.disconnected:
          setFilteredPlatforms(platformSettings?.filter((item) => !item.connected));
          return;
        default:
          setFilteredPlatforms(platformSettings);
      }
    },
    [filteredPlatforms]
  );

  const findSocialHandle = useCallback(
    (e) => {
      if (!platformSettings) {
        return;
      }
      const foundSocials = platformSettings.filter((item) => {
        const fullSocialName = intl.formatMessage({
          id: `auth-methods.${item.provider}`
        });

        return fullSocialName.toLowerCase().includes(e.target.value.toLowerCase());
      });

      setFilteredPlatforms(foundSocials);
    },
    [platformSettings]
  );

  return (
    <>
      <SearchBarWrapper>
        <RigthContentWrapper>
          <SocialModeSelectWrapper>
            <Select
              onChange={handleSelectMode}
              options={selectSocialOptions}
              placeholder={SocialSelectTypes.all}
              size="sm"
              value={connectionsMode}
              data-test-id="cross-social-filter"
            />
          </SocialModeSelectWrapper>
          <SocialSearchInput
            extraAlign="left"
            onChange={findSocialHandle}
            placeholder={intl.formatMessage({ id: 'social-connection.search.placeholder' })}
            extra={Search}
            data-test-id="cross-social-search"
            uiSize="sm"
          />
        </RigthContentWrapper>
      </SearchBarWrapper>
      <CardWrapper isFetching={platformSettingsFetching} data-test-id="cross-social-list">
        {platformSettingsFetching ? (
          <Preloader />
        ) : (
          filteredPlatforms
            ?.filter((_, index) => isShowMoreMode || index < 12)
            .map((item) => {
              return (
                <SocialNetworkCard
                  projectId={projectId}
                  socialData={item}
                  setSocials={setPlatformSettings}
                  platformCard
                  key={item.provider}
                />
              );
            })
        )}
      </CardWrapper>
      <ShowModeButtonWrapper>
        {filteredPlatforms && filteredPlatforms.length > 12 && (
          <Button
            onClick={() => setIsShowMoreMode((prev) => !prev)}
            look="tertiary"
            palette="muted"
            size="md">
            {
              intl.messages[
                `social-connection.card.button.${isShowMoreMode ? 'show-less' : 'show-more'}`
              ]
            }
          </Button>
        )}
      </ShowModeButtonWrapper>
    </>
  );
};

export default PlatformsList;
